import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReportSubstantiation } from 'src/api';
import { DefaultOption, FormInput } from 'src/components/molecules';
import { ReassignReportModal } from 'src/components/organismsLegacy/ReportDetails/components';
import {
  ReportDetailsVm,
  reportDetailsVm,
  updateDeterminedCategory,
  updateReportAssignee,
  updateSubmissionDate,
  updateSubstantiationStatus,
} from 'src/components/pages/Case/Case.slice';
import { AppDispatch } from 'src/store';
import {
  FormInputVariant,
  FormState,
  SubmissionModeForDisplay,
} from 'src/types';
import { ReportDetailsContainerStyled } from './ReportDetailsContainer.styles';
import { useUpdateReportDetails } from './hooks/useUpdateReportDetails';

interface Props {
  reportId: string;
  testVm?: ReportDetailsVm;
  'data-testid'?: string;
}

export const ReportDetailsContainer = (props: Props) => {
  const { reportId, testVm } = props;

  const dispatch = useDispatch<AppDispatch>();
  const [showPicker, setShowPicker] = useState(false);

  const vm = useSelector(reportDetailsVm({ reportId, testVm }));

  const {
    mutationReassignReportAssignee,
    mutationUpdateDeterminedCategory,
    mutationUpdateSubmissionDate,
    mutationUpdateSubstantiationStatus,
  } = useUpdateReportDetails();

  const validUsers = vm.assignableReportManagers;
  const validUserOptions = (validUsers ?? []).map((user) => {
    if (user.displayName?.length) {
      return {
        label: user.displayName,
        value: user.id,
      };
    }
    if (user.name?.length) {
      return {
        label: user.name,
        value: user.id,
      };
    }
    return {
      label: '',
      value: user.id,
    };
  });

  const today = new Date().toISOString().split('T')[0];
  const testId = props['data-testid']
    ? `-${reportId}-${props['data-testid']}`
    : `-${reportId}`;

  const viewDetails = vm.permissions.canViewReportDetails;
  const editDetails = vm.permissions.canEditReportDetails;
  const setFormState = (defaultState: FormState) =>
    editDetails
      ? defaultState
      : viewDetails
        ? FormState.READONLY
        : FormState.DISABLED;

  const [openReportReassignModal, setOpenReportReassignModal] = useState(false);
  const [updatedReportAssignee, setUpdatedReportAssignee] = useState<
    number | null
  >(null);

  const isManualIntake = vm.submissionRoute.includes(
    SubmissionModeForDisplay.MANUAL_INTAKE,
  );

  const renderMiReportInput =
    (vm.miReportSource !== '' &&
      vm.submissionRoute.includes(SubmissionModeForDisplay.BULK_IMPORT)) ||
    isManualIntake;

  const formState = setFormState(vm.submissionDateVariant);

  return (
    <ReportDetailsContainerStyled
      {...props}
      data-testid={`report-details-container${testId}`}
    >
      <FormInput
        isRequired={false}
        label={isManualIntake ? 'Reported to' : 'Report Assignee'}
        data-testid="update-assignee-dropdown"
        variant={FormInputVariant.DROPDOWN}
        state={setFormState(vm.reportAssigneeState)}
        options={validUserOptions}
        value={vm.reportedTo}
        onChange={(option) => {
          if (option) {
            setUpdatedReportAssignee(Number((option as DefaultOption).value));
            setOpenReportReassignModal(true);
          }
        }}
        isSearchable
      />
      <FormInput
        isRequired={false}
        label="Submission date"
        data-testid="submission-date-form-input"
        variant={FormInputVariant.INPUT}
        state={formState}
        onBlur={(e) => {
          if (e.target.value === vm.submissionDate) return;
          dispatch(
            updateSubmissionDate({
              mutationUpdateSubmissionDate,
              report: reportId,
              submittedAt: new Date(e.currentTarget.value).toISOString(),
            }),
          );
        }}
        type={viewDetails ? 'date' : 'text'}
        max={today}
        defaultValue={vm.submissionDate}
        onChange={() => {
          setShowPicker(false);
        }}
        onClick={(e) => {
          if (
            formState === FormState.DISABLED ||
            formState === FormState.READONLY
          )
            return;
          if (!editDetails) return;
          if (showPicker) {
            e.preventDefault();
            e.stopPropagation();
            setShowPicker(false);
            if (e.currentTarget.value === vm.submissionDate) return;
            dispatch(
              updateSubmissionDate({
                mutationUpdateSubmissionDate,
                report: reportId,
                submittedAt: e.currentTarget.value,
              }),
            );
            return;
          }
          e.currentTarget.showPicker();
          setShowPicker(true);
        }}
      />
      <FormInput
        isRequired={false}
        label="Reported category"
        data-testid="reported-category-form-input"
        variant={FormInputVariant.DROPDOWN}
        state={setFormState(FormState.READONLY)}
        value={vm.reportedCategory}
        isMulti={Boolean(viewDetails)}
        options={[]}
        onChange={() => {}}
      />
      <FormInput
        isRequired={false}
        label="Determined category"
        data-testid="determined-category-form-input"
        variant={FormInputVariant.DROPDOWN}
        state={setFormState(FormState.DEFAULT)}
        options={vm.determinedCategories}
        onChange={(option) => {
          dispatch(
            updateDeterminedCategory({
              determinedCategoryId: (option as DefaultOption).value as number,
              report: reportId,
              mutationUpdateDeterminedCategory,
            }),
          );
        }}
        value={vm.determinedCategory}
      />
      <FormInput
        isRequired={false}
        label="Substantiation status"
        data-testid="substantiation-status-form-input"
        variant={FormInputVariant.DROPDOWN}
        state={setFormState(FormState.DEFAULT)}
        options={Object.values(ReportSubstantiation).map((status) => ({
          value: status,
          label: status,
        }))}
        onChange={(option) => {
          dispatch(
            updateSubstantiationStatus({
              mutationUpdateSubstantiationStatus,
              report: reportId,
              newSubstantiationStatus: (option as DefaultOption)
                .value as string,
              caseId: vm.caseId,
            }),
          );
        }}
        value={vm.substantiationStatus}
      />
      {renderMiReportInput && (
        <FormInput
          isRequired={false}
          label="Source"
          data-testid="report-source-form-input"
          variant={FormInputVariant.INPUT}
          state={setFormState(FormState.READONLY)}
          type="text"
          defaultValue={vm.miReportSource}
        />
      )}
      <FormInput
        isRequired={false}
        label="Submission route"
        data-testid="report-submission-route"
        variant={FormInputVariant.INPUT}
        state={setFormState(FormState.READONLY)}
        type="text"
        defaultValue={vm.submissionRoute}
      />
      <ReassignReportModal
        setOpen={setOpenReportReassignModal}
        open={openReportReassignModal}
        onConfirm={() => {
          if (updatedReportAssignee) {
            dispatch(
              updateReportAssignee({
                mutationReassignReportAssignee,
                report: reportId,
                newReportAssigneeId: updatedReportAssignee,
                caseId: vm.caseId,
              }),
            );
          }
          setOpenReportReassignModal(false);
        }}
        onCancel={() => {
          setUpdatedReportAssignee(null);
          setOpenReportReassignModal(false);
        }}
      />
    </ReportDetailsContainerStyled>
  );
};
